<template>
  <header class="hero" :class="`content-block-${img?.value ? 'full' : 'large'}`">
    <HeroesIntroHero :icon="intro.icon" :title="intro.title" :body="intro.body" />
    <div v-if="img?.value && img.value.url" class="hero-img">
      <img
        width="1920"
        fetchpriority="high"
        v-bind="useEpiAttrs(img)"
        :src="img.value.url + '?w=1920'"
        :alt="img.value.alt ?? ''" />
    </div>
    <article v-if="article?.links && article.links.length" class="block-vacancies">
      <h3 v-if="article.title" v-bind="useEpiAttrs(article.title)">
        {{ article.title.value }}
      </h3>
      <ul v-if="article.links">
        <li v-for="(navLink, index) in article.links" :key="navLink.name ?? index">
          <LinksIconTextLink
            icon="arrow-right"
            :text="navLink.name ?? ''"
            :link="navLink.url ?? ''"
            icon-color="oker"
            text-color="navy-blue"
            :has-hover-underline="true"
            :has-animate-icon="true"
            :has-font-bold="true" />
        </li>
      </ul>
      <LinksIconTextLink
        v-if="article.readMoreTitle?.value"
        icon="arrow-right"
        :text="article.readMoreTitle"
        :link="article.readMoreLink ?? ''"
        icon-color="oker"
        text-color="navy-blue"
        :has-hover-underline="true"
        :has-animate-icon="true"
        :has-font-bold="true" />
    </article>
  </header>
</template>

<script setup lang="ts">
import type { ImgTextBlock } from '@base/types/models';
import type {
  ContentAreaLinksBlockResponseModel,
  FieldResponseModelOfImageResponseModel,
} from '@api';

interface MainHero {
  mainHero: {
    intro: ImgTextBlock;
    img?: FieldResponseModelOfImageResponseModel | null | undefined;
    article?: ContentAreaLinksBlockResponseModel | null | undefined;
  };
}

const { mainHero } = defineProps<MainHero>();
const { intro, img, article } = mainHero;
</script>

<style lang="scss" scoped>
.hero {
  padding: 0;
  margin-bottom: px(24);
  background-color: var(--color-blue);
  grid-row: 1 / 2;

  .hero-img {
    position: relative;
    width: 100%;

    &::before {
      content: '';
      display: block;
      width: 100%;
      padding-top: 56.25%;
    }

    img {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  :deep(p) {
    font-size: 0.9rem;
  }

  .block-vacancies {
    color: var(--color-blue);
    background-color: var(--color-white);
    border-radius: px(12);
    padding: px(16) px(24);

    ul {
      @include reset-list;
    }

    > .link {
      align-self: flex-end;
      margin-top: px(40);
    }
  }

  &.content-block-full {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-template-rows: repeat(3, auto);

    .hero-intro {
      grid-column: 1 / 13;
      grid-row: 1 / 2;
    }

    .hero-img {
      position: relative;
      grid-row: 3 / 4;
      grid-column: 1 / 13;
    }

    .block-vacancies {
      display: flex;
      flex-direction: column;
      grid-row: 2 / 3;
      grid-column: 2 / 12;
      margin-bottom: px(24);
    }
  }

  @include media-breakpoint-up(lg) {
    position: relative;
    margin-bottom: px(42);
    background: transparent;

    .hero-img {
      height: px(627);
    }

    .block-vacancies {
      padding: px(48) px(80);
    }

    &.content-block-full {
      padding-bottom: 0;

      .hero-intro {
        grid-column: 2 / 12;
        margin-bottom: px(64);
      }

      .hero-img {
        z-index: -10;
        grid-row: 1 / 4;
        margin-top: px(148);
      }

      .block-vacancies {
        grid-column: 2 / 12;
      }
    }

    :deep(p) {
      font-size: px(20.8);
    }
  }

  @include media-breakpoint-up(xl) {
    &.content-block-full {
      .block-vacancies {
        grid-column: 9 / 12;
      }
    }
  }
}
</style>
